import type { FC, ReactNode } from 'react'
import type { ListingMedia, ListingPlaceholder } from '@/types/marketplace'

import { useState } from 'react'
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react'

import { cn } from '@/lib/utils'
import { ListingPlaceholderArt } from '@/components/marketplace/listing-placeholder-art'

interface ListingGalleryProps {
    media: Array<ListingMedia>
    title: string
    /** The saved appearance, used only when this listing has no photo at all. */
    placeholder: ListingPlaceholder | null
    categorySlug: string | null
    /** Stable per listing, so the art never changes between visits. */
    seed: string
    dimmed?: boolean
}

export const ListingGallery: FC<ListingGalleryProps> = ({
    media,
    title,
    placeholder,
    categorySlug,
    seed,
    dimmed
}): ReactNode => {
    const [index, setIndex] = useState(0)

    /**
     * No photograph. The listing still gets the designed plate it was given at
     * enrichment time rather than an empty grey box, with the fact that there
     * is no photo said plainly underneath it.
     */
    if (media.length === 0) {
        return (
            <div className='mta-border-strong relative aspect-square w-full overflow-hidden rounded-mta-sm sm:aspect-4/3'>
                <ListingPlaceholderArt
                    placeholder={placeholder}
                    categorySlug={categorySlug}
                    seed={seed}
                    iconClassName='size-16 sm:size-20'
                />

                <span className='type-label absolute bottom-3 right-3 flex items-center gap-1.5 rounded-mta-xs bg-paper px-2.5 py-1.5 text-[11px] leading-none text-ink-900'>
                    <ImageOff className='size-3.5' aria-hidden='true' />
                    No photo provided
                </span>
            </div>
        )
    }

    const active = media[Math.min(index, media.length - 1)]
    const go = (next: number): void => setIndex((next + media.length) % media.length)

    return (
        <div className='flex flex-col gap-3'>
            {/* Paper plate inside a thick black rule, product floated with air on all sides. */}
            <div className='mta-border-strong relative grid aspect-square w-full place-items-center overflow-hidden rounded-mta-sm bg-surface-muted p-6 sm:aspect-4/3 sm:p-10 lg:p-14'>
                <img
                    src={active.storageUrl}
                    alt={media.length > 1 ? `${title}, photo ${index + 1} of ${media.length}` : title}
                    width={1200}
                    height={900}
                    loading='eager'
                    decoding='async'
                    className={cn(
                        'max-h-full w-auto max-w-full object-contain',
                        dimmed && 'opacity-60'
                    )}
                />

                {media.length > 1 && (
                    <>
                        <button
                            type='button'
                            onClick={() => go(index - 1)}
                            aria-label='Previous photo'
                            className='mta-border mta-focus absolute left-3 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-mta-xs bg-paper text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper'
                        >
                            <ChevronLeft className='size-5' aria-hidden='true' />
                        </button>

                        <button
                            type='button'
                            onClick={() => go(index + 1)}
                            aria-label='Next photo'
                            className='mta-border mta-focus absolute right-3 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-mta-xs bg-paper text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper'
                        >
                            <ChevronRight className='size-5' aria-hidden='true' />
                        </button>

                        <span className='type-label mta-border absolute bottom-3 right-3 rounded-mta-xs bg-ink-900 px-2.5 py-1 text-[10px] leading-none text-paper'>
                            {`${index + 1} / ${media.length}`}
                        </span>
                    </>
                )}
            </div>

            {media.length > 1 && (
                <ul className='flex gap-2 overflow-x-auto no-scrollbar'>
                    {media.map((item, itemIndex) => (
                        <li key={item.id}>
                            <button
                                type='button'
                                onClick={() => setIndex(itemIndex)}
                                aria-label={`Show photo ${itemIndex + 1}`}
                                aria-current={itemIndex === index}
                                className={cn(
                                    'mta-focus size-16 overflow-hidden rounded-mta-xs border-2 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)]',
                                    itemIndex === index ? 'border-brand-red' : 'border-ink-900/25 hover:border-ink-900'
                                )}
                            >
                                <img
                                    src={item.storageUrl}
                                    alt=''
                                    width={128}
                                    height={128}
                                    loading='lazy'
                                    decoding='async'
                                    className='size-full object-cover'
                                />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
